import { Injectable } from '@angular/core';
import { UploadInput } from 'ngx-uploader';
import { Observable, Subject, of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import * as moment from 'moment';

import { ApiService } from './../api.service';
import { HandleFileService } from './handle-file.service';
import { AuthService } from './../auth.service';
import { PropertyService } from './property.service';
import { ErrorHandlerService } from './error-handler.service';
import { environment } from '../../environments/environment';
import { CreateTransactionParams, BillToOtherParty } from '../interfaces/transaction';
import { TransactionType, TransactionTypes } from '../models/transactions-enum';
import { Property } from '../models/property';
import { DataObject } from '../interfaces/data-objects';

@Injectable({
  providedIn: 'root'
})
export class TransactionService {

  _submitCache = {};
  expenseSubmitSubject = new Subject();

  private billToOtherPartySubject: Subject<BillToOtherParty> = new Subject();
  billToOtherPartySubject$ = this.billToOtherPartySubject.asObservable();

  constructor(
    private api: ApiService,
    private handleFile: HandleFileService,
    private auth: AuthService,
    private propertyService: PropertyService,
    private errorHandler: ErrorHandlerService) {
  }

  createTransaction(params: CreateTransactionParams) {
    const apiURL = `/api/transactions/${this.api.getTransactionEndpoint({ 'transaction_type': params.transaction_type })}/`;

    let localParams: CreateTransactionParams = {
      opportunity_id: '',
      rep_role: '',
      rate_type: '',
      team: null,
      installment_id: '',
      is_deeplinking_transaction: false,
      transaction_type: null,
      capital_advisory: '',
      advisory_team_option: '',
      advisory_regulation: ''
    };

    let finalParams: CreateTransactionParams = { ...localParams, ...params };

    // INFO: Call createProperty API for all Transaction Types but 'Expenses'
    return this.api.post(apiURL, finalParams)
      .pipe(
        mergeMap((result) => {
          let transactionResult = {
            transactionId: result['id'],
            opportunityId: finalParams.opportunity_id,
            propertyId: null,
            property: {}
          };
          if (finalParams.transaction_type !== TransactionTypes.EXPENSE && finalParams.transaction_type !== TransactionTypes.CONSULTING) {
            let property$: Observable<Property> = this.propertyService.createProperty(result.id);
            return property$.pipe(
              map((propertyResult: Property) => {
                return ({ ...transactionResult, 'property': propertyResult, 'propertyId': propertyResult.id });
              })
            );
          } else {
            return of(transactionResult);
          }
        })
      );
  }

  getTransaction(transactionId: number, transactionType: TransactionType): Observable<any> {
    return this.api.get(`/api/transactions/${this.api.getTransactionEndpoint({'transaction_type': transactionType})}/${transactionId}/`); 
  }

  saveTransaction(transaction: any): Observable<any> {
    return this.api.put(`/api/transactions/${this.api.getTransactionEndpoint({'transaction_type': transaction.transaction_type})}/${transaction.id}/`, transaction);
  }

  deleteTransaction(query) {
    return this.api.delete(`/api/transactions/${query}/`);
  }

  setBillToOtherPartySubject(object: BillToOtherParty): void {
    this.billToOtherPartySubject.next(object);
  }

  submitTransaction(transaction: any) {
    const submitTimestamp = Date.now();
    const date = new Date(submitTimestamp);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);   // getMonth() is zero based
    const dayOfMonth = ('0' + date.getDate()).slice(-2);
    const transactionEndpoint = this.api.getTransactionEndpoint(transaction);

    let transactionAction = 'submit';
    let doubleApprovalStatus: any = null;
    let doubleApprovalParameters: any = null;

    if (transaction.double_approval) {
      doubleApprovalStatus = this.getDoubleApprovalStatus(transaction);
      transactionAction = this.getSubmitAction(transaction.double_approval_turn, doubleApprovalStatus);

      if (['submitted', 'approve'].includes(doubleApprovalStatus)) {
        doubleApprovalParameters = {
          'exec_approver_emplid': this.getExecApproverEmplid(transaction),
          'approval_status': doubleApprovalStatus,
        };
      } else {
        doubleApprovalParameters = {
          'approval_status': doubleApprovalStatus,
          'reason_notes': transaction.reason_notes || ''
        };
      }
    }

    return this.api.post(`/api/transactions/${transactionEndpoint}/${transaction.id}/${transactionAction}/`,
      { localDate: date.getFullYear() + '-' + month + '-' + dayOfMonth, ...doubleApprovalParameters }
    )
    .pipe(
      map(result => {
        this._submitCache[`${submitTimestamp}`] = result;
        result.timestamp = submitTimestamp;
        return result;
      })
    );
  }

  /**
   * INFO: It is part of the Double Approval logic
   * @returns The Approval Status: Submitted, Approved or Rejected
   */
  getDoubleApprovalStatus(transaction: any): string {
    if (transaction.double_approval_status && transaction.double_approval_status === 'rejected') {
      return transaction.double_approval_status;
    }
    return transaction.double_approval_turn === 'broker' ? 'submitted' : 'approve';
  }

  /**
   * INFO: It is part of the Double Approval logic
   * @returns submit or doubleapproval
   */
  getSubmitAction(doubleApprovalTurn: string, doubleApprovalStatus: string): string {
    switch (doubleApprovalTurn) {
      case 'broker':
      case 'executive1':
        return 'doubleapproval';
      case 'executive2':
        return doubleApprovalStatus === 'rejected' ? 'doubleapproval' : 'submit';
      default:
        return 'submit';
    }
  }

  /**
   * INFO: It is part of the Double Approval logic,
   * to be used when calling doubleapproval or submit APIs
   * @param transaction
   * @returns EmplId based on the Double Approval Turn
   */
  getExecApproverEmplid(transaction: any): string | null {
    switch (transaction.double_approval_turn) {
      case 'broker':
        return transaction.executive_approver1;
      case 'executive1':
        return transaction.executive_approver2;
      default:
        return null;
    }
  }

  getTransactionResult(timestamp) {
    return this._submitCache[timestamp];
  }

  validateTransaction(transaction) {
    const transactionEndpoint = this.api.getTransactionEndpoint(transaction);
    return this.api.post(`/api/transactions/${transactionEndpoint}/${transaction.id}/validate/`, {});
  }

  getLandingTopSection(start_date, end_date) {
    let url = '/api/transactions/topsection/';
    if (start_date && end_date) {
      url += '?start_date=' + start_date + '&end_date=' + end_date;
    }
    return this.api.get(url)
    .pipe(
      map(response => {
        return response.topSection;
      })
    );
  }

  getTransactionsCount(status) {
    return this.api.get(`/api/transactions/subsection/?status=${status}`)
    .pipe(
      map(response => {
        return response.transactionTypes;
      })
    );
  }

  getLandingPropertyData(
    status,
    transType,
    startDate?: any,
    endDate?: any,
    sortBy?,
    sortDirection?: 'asc' | 'desc'
  ) {
    let params = `page_size=${10000}&status=${status}&trans=${transType}`;
    if (startDate && endDate) {
      params += `&start_date=${startDate}&end_date=${endDate}`;
    }
    return this.api.get(`/api/transactions/landing/?${params}`)
    .pipe(
      map(response => {
        if (response?.error?.status || response?.status >= 400) {
          throw response;
        }
        return response.results;
      }),
      catchError(error => {
        return this.errorHandler.handleError(error, []);
      })
    );
  }

  creditandRebillTransaction(transactionData: any, invoker: 'rebill' | 'credit', myFinance: string) {
    const invoiceRow = transactionData['invoiceRow'];
    const submitTimestamp = Date.now();
    const date = new Date(submitTimestamp);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);   // getMonth() is zero based
    const dayOfMonth = ('0' + date.getDate()).slice(-2);
    const country: any = window.localStorage.getItem('country');
    let payload;
    if (invoker === 'credit') {
      const url = `/api/transactions/${invoker}/${invoiceRow['id']}/`;
      if (country === 'IND') {
        if (myFinance === 'recd') {
          const { adjustment: reason, notes } = transactionData;
          payload = { reason, notes };
        } else if (myFinance === 'peoplesoft') {
          const { adjustment: reason, notes, billing_entity, billing_office, tax_rate_code, cess_code, approver, approver_name } = transactionData;
          const { SHIP_TO_CUST_ID, SHIP_TO_ADDR_NUM } = transactionData.invoiceRow;
          payload = { reason, notes, billing_entity, SHIP_TO_CUST_ID, SHIP_TO_ADDR_NUM, billing_office, tax_rate_code, cess_code, approver };
          
          // Add business approver fields if they exist
          if (approver && approver_name) {
            payload.approver = approver;
            payload.approver_name = approver_name;
          }
        }
      } else if (country === 'JPN') {
        if (myFinance === 'peoplesoft') {
          const { adjustment: reason, notes, billing_entity, approver, approver_name } = transactionData;
          const { invoice_form } = transactionData.invoiceRow;
          payload = { reason, notes, billing_entity, approver, invoice_form };
          
          // Add business approver fields if they exist
          if (approver && approver_name) {
            payload.approver = approver;
            payload.approver_name = approver_name;
          }
        } else {
          const { adjustment: reason, notes, billing_entity, approver, approver_name } = transactionData;
          payload = { reason, notes, billing_entity, approver };
          
          // Add business approver fields if they exist
          if (approver && approver_name) {
            payload.approver = approver;
            payload.approver_name = approver_name;
          }
        }
      } else if (['AUS', 'NZL'].includes(country)) {
        if (myFinance === 'peoplesoft') {
          const { adjustment: reason, notes, billing_entity, approver, approver_name } = transactionData;
          const { office } = transactionData.invoiceRow;
          payload = { reason, notes, billing_entity, approver, office };
          
          // Add business approver fields if they exist
          if (approver && approver_name) {
            payload.approver = approver;
            payload.approver_name = approver_name;
          }
        } else {
          const { adjustment: reason, notes, billing_entity, approver, approver_name } = transactionData;
          payload = { reason, notes, billing_entity, approver };
          
          // Add business approver fields if they exist
          if (approver && approver_name) {
            payload.approver = approver;
            payload.approver_name = approver_name;
          }
        }
      } else {
        const { adjustment: reason, notes, billing_entity, approver, approver_name } = transactionData;
        payload = { reason, notes, billing_entity, approver };
        
        // Add business approver fields if they exist
        if (approver && approver_name) {
          payload.approver = approver;
          payload.approver_name = approver_name;
        }
      }
      return this.api.post(url, { localDate: date.getFullYear() + '-' + month + '-' + dayOfMonth, ...payload })
        .pipe(map(response => {
          this._submitCache[`${submitTimestamp}`] = response;
          response.timestamp = submitTimestamp;
          return response;
        }));
    } else if (invoker === 'rebill') {
      const adjustment = transactionData['adjustment'];
      const notes = transactionData['notes'] || null;
      payload = { reason: adjustment, notes: notes };
      const url = `/api/transactions/${invoker}/${invoiceRow['id']}/`;
      return this.api.post(url, payload).pipe(map(response => response));
    }

  }

  submitPartialCredit(transactionData: any): Observable<any> {
    const invoiceRow = transactionData?.invoiceRow;
    const submitTimestamp = Date.now();
    const localDate = this.formatDateForSubmission(new Date(submitTimestamp));
    const { adjustment: reason, notes, tax_rate_code, cess_code } = transactionData;
    const payload = { 
      reason, 
      notes, 
      tax_rate_code, 
      cess_code,
      localDate
    };
    const url = `/api/transactions/partial_credit/${invoiceRow.id}/`;
    return this.api.post(url, payload).pipe(
      map(response => {
        this._submitCache[`${submitTimestamp}`] = response;
        return { ...response, timestamp: submitTimestamp };
      })
    );
  }

  formatDateForSubmission(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  redirecttoTransactionPage(statusName) {
    if (
      statusName !== 'Work in Progress' &&
      statusName !== 'Rebill' &&
      statusName !== 'Error' &&
      statusName !== 'Adjust Bill Lines' &&
      statusName !== 'Denied'
    ) {
      return 'review';
    }
    return null;
  }

  sendProforma(transactionId, emailId) {
    return this.api.post(`/api/transactions/email/${transactionId}/`, { 'email': emailId })
    .pipe(
      map(response => {
        return response;
      })
    );
  }

  sendAdvisor(transactionId, emailId, lang?: string) {
    const payload: any = { 'email': emailId };
    if (lang) {
      payload['lang'] = lang;
    }
    return this.api.post(`/api/transactions/invoice_email/${transactionId}/`, payload)
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  downloadProforma(transactionId) {
    return this.handleFile.get(`/api/transactions/proforma/${transactionId}/`)
      .pipe(
        map(response => {
          if (response) {
            return new Blob([response], {
              type: 'application/pdf'
            });
          }
        })
      );
  }

  downloadAdvisor(transactionId, lang?: string) {
    const url = lang
      ? `/api/transactions/invoice/${transactionId}/?lang=${encodeURIComponent(lang)}`
      : `/api/transactions/invoice/${transactionId}/`;
    return this.handleFile.get(url)
      .pipe(
        map(response => {
          if (response) {
            return new Blob([response], {
              type: 'application/pdf'
            });
          }
        })
      );
  }

  canEditTransaction(transaction) {
    const transactionEndpoint = this.api.getTransactionEndpoint({'transaction_type': transaction.transaction_type});
    return this.api.get(`/api/transactions/${transactionEndpoint}/${transaction.id}/`)
      .pipe(
        map(response => {
          if (response) {
            return response.can_edit;
          }
        })
      );
  }

  getNotificationCount() {
    if (this.auth.getAuthToken()) {
      return this.api.get(`/api/transactions/notification/count/`);
    } else {
      return of(0);
    }
  }

  getNotificationRecords() {
    return this.api.get(`/api/transactions/notification/data/`)
      .pipe(
        map(response => {
          if (response) {
            return response.result;
          } else {
            return [];
          }
        })
      );
  }

  dismissNotification(id) {
    return this.api.get(`/api/transactions/notification/delete/${id}/`);
  }

  dismissAllNotifications() {
    return this.api.get(`/api/transactions/notification/delete/all/`);
  }

  /* Mini-Dashboard */
  getMiniDashboard(transaction) {
    const transactionEndpoint = this.api.getTransactionEndpoint(transaction);
    return this.api.get(`/api/transactions/${transactionEndpoint}/${transaction.id}/dash/`)
      .pipe(
        map(response => {
          if (response) {
            return response;
          }
        })
      );
  }

  downloadInvoice(transactionId, invoiceId, invoiceDate) {
    return this.handleFile.download(`/api/transactions/invoiced/${transactionId}/`, {
      'invoice_id': invoiceId,
      'date': invoiceDate
    })
    .pipe(
      map(response => {
        if (response) {
          return new Blob([response], {
            type: 'application/pdf'
          });
        }
      })
    );
  }

  residownloadInvoice(transactionIds) {
    const transactionIdString = transactionIds.join(',');
    return this.handleFile
      .get(`/api/transactions/zip_invoices/?transaction_ids=` + transactionIdString)
      .pipe(
        map((response) => {
          if (response) {
            return new Blob([response], {
              type: "application/zip",
            });
          }
        })
      );
  }

  residownloadSummary(transactionIds) {
    return this.handleFile
      .download(`/api/transactions/resi-summary/`, {'transaction_ids': transactionIds})
      .pipe(
        map((response) => {
          if (response) {
            return new Blob([response], {
              type: "application/pdf",
            });
          }
        })
      );
  }

  getPastInvoicesData(transactionId) {
    return this.api.get(`/api/transactions/invoiced/${transactionId}/`)
    .pipe(
      map(response => {
        if (response) {
          return response;
        }
      })
    );
  }

  toggleSubmitButtonInExpense(expenseValid) {
    this.expenseSubmitSubject.next(expenseValid);
  }

  // Adjust transaction (to get all data)
  getInternalAdjustData(transactionId) {
    return this.api.get(`/api/transactions/adjust/${transactionId}/`)
    .pipe(
      map(response => {
        if (response) {
          return response;
        }
      })
    );
  }

  // Adjust transaction (to create empty fee splits)
  createInternalAdjustData(transactionId, feeSplit = {}) {
    return this.api.post(`/api/transactions/adjust/${transactionId}/`, feeSplit)
    .pipe(
      map(response => {
        if (response) {
          return response;
        }
      })
    );
  }

  // Adjust transaction (to update fee splits)
  saveInternalAdjustData(transactionId, feeSplit) {
    return this.api.put(`/api/transactions/adjust_update/${transactionId}/fee_split/${feeSplit.id}/`, feeSplit)
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  // Adjust transaction (to delete fee splits)
  deleteInternalAdjustData(transactionId, feeSplitId) {
    return this.api.delete(`/api/transactions/adjust_update/${transactionId}/fee_split/${feeSplitId}/`)
    .pipe(
      map(response => {
        if (response) {
          return response;
        }
      })
    );
  }

  submitInternalAdjustData(transaction) {
    const transactionEndpoint = this.api.getTransactionEndpoint(transaction);
    return this.api.post(`/api/transactions/${transactionEndpoint}/${transaction.id}/submit_adjustment/`, transaction)
    .pipe(
      map(response => {
        if (response) {
          return response;
        }
      })
    );
  }

  submitNonFinancialTransaction(transaction) {
    const submitTimestamp = Date.now();
    const transactionEndpoint = this.api.getTransactionEndpoint(transaction);
    return this.api.post(`/api/transactions/${transactionEndpoint}/${transaction.id}/submit_invoiced/`, {})
      .pipe(
        map(result => {
          this._submitCache[`${submitTimestamp}`] = result;
          result.timestamp = submitTimestamp;
          return result;
        })
      );
  }

  downloadDocument(file) {
    return this.handleFile.get(`/api/transactions/download/${file.id}/`)
    .pipe(
      map(response => {
        if (response) {
          const contentType = this.handleFile.getContentType(file.file_name);
          return new Blob([response], { 'type': contentType });
        }
      })
    );
  }

  uploadTransactionFile(transactionId) {
    const authHeaders = {};
    const mockToken = environment.mockToken;
    const jwtToken = this.auth.getAuthToken();

    if (mockToken && mockToken !== '') {
      authHeaders['Authorization'] = 'Token ' + mockToken;
    } else if (jwtToken) {
      authHeaders['Authorization'] = 'JWT ' + jwtToken;
    }

    const event: UploadInput = {
      type: 'uploadAll',
      url: this.api.getPortwithURL(`/api/transactions/${transactionId}/upload/`),
      method: 'POST',
      // concurrency: 4,
      headers: authHeaders
    };

    return event;
  }

  deleteTransactionFile(transactionId: number, fileId: number) {
    return this.api.delete(`/api/transactions/${transactionId}/upload/${fileId}/`);
  }

  wipeOutDisbursementsFields(transactionId: number) {
    return this.api.post(`/api/transactions/${transactionId}/disbursement/`, {});
  }

  getFeeEarnersforDisbursements(transactionId: number) {
    return this.api.get(`/api/transactions/${transactionId}/fee_splits/brokers/`)
      .pipe(
        map(response => {
          return response.result;
        })
      );
  }

  getBrokerOffice(feeSplit): string {
    let brokerOffice = '';
    if (feeSplit.bill_external) {
      brokerOffice = feeSplit.company_office;
    } else {
      brokerOffice = `${feeSplit.functional_unit.name} - ${feeSplit.managing_office.name}`;
    }
    return brokerOffice || '';
  }
  
  downloadFeeCalculation(transactionId) {
    return this.handleFile.get(`/api/transactions/fee_calculations/${transactionId}/`)
      .pipe(
        map(response => {
          if (response) {
            return new Blob([response], {
              type: 'application/pdf'
            });
          }
        })
      );
  }

  // TODO: Remove this function and implement the new pure pipe customer-address-tooltip-fn.pipe
  getCustomerAddressTooltip(customer: DataObject): string {
    if (customer && customer.addresses && customer.addresses.addresses[0]) {
      const _address = customer.addresses.addresses[0];
      const _address1 = _address.address1.trim() && _address.address1 !== 'NULL' ? _address.address1 + `\n` : '';
      const _address2 = _address.address2.trim() && _address.address2 !== 'NULL' ? _address.address2 + `\n` : '';
      const _city = _address.city.trim() && _address.city !== 'NULL' ? _address.city + `\n` : '';
      const _country = _address.country.trim() && _address.country !== 'NULL' ? _address.country.trim() + '\n' : '';
      const _postal = _address.postal.trim() && _address.postal !== 'NULL' ? _address.postal + `\n` : '';
      const _region = _address.region && _address.region.trim() ? _address.region + `\n` : '';
      const _clientABN_header = _address.country && ['KOR', 'TWN', 'THA', 'PHL', 'VNM'].includes(_address.country.trim()) ? 'VAT: ' : 'ABN: ';
      const _client_abn = customer.client_abn && customer.client_abn.trim() ? _clientABN_header + customer.client_abn + '\n' : '';
      const _gstin = _address.stx_cst_rgstn && _address.stx_cst_rgstn.trim() ? 'GSTIN: ' + _address.stx_cst_rgstn : '';
      let _state = '', county = '';
      if (_address.country && ['CHN', 'JPN', 'TWN'].includes(_address.country.trim())) {
        _state = _address.state_descr.trim() && !['NULL', 'None'].includes(_address.state_descr) ? _address.state_descr + `\n` : '';
        if (_address.county) {
          county = _address.county.trim() && !['NULL', 'None'].includes(_address.county) ? _address.county + `\n` : '';
        }
        // tslint:disable-next-line:max-line-length
        return `${customer.cust_id}\n${customer.name1} ${customer.name2} ${customer.name3}\n${_address1}${_address2}${_city}${_state}${county}${_region}${_postal}${_country}${_client_abn}${_gstin}`;
      } else {
        _state = _address.state.trim() && !['NULL', 'None'].includes(_address.state) ? _address.state + `\n` : '';
        // tslint:disable-next-line:max-line-length
        return `${customer.cust_id}\n${customer.name1} ${customer.name2} ${customer.name3}\n${_address1}${_address2}${_city}${_state}${_region}${_postal}${_country}${_client_abn}${_gstin}`;
      }
    }
    return '';
  }

  getTotalUserCountries() {
    return this.api.get(`/api/activedirectory/getcountries/`)
    .pipe(
      map(response => {
        if (response) {
          return response;
        }
      })
    );
  }

  dateValidator(country) {
    let regExp;
    if (country === 'JPN') {
      regExp = /^\d{4}\/(0?[1-9]|1[012])\/(0?[1-9]|[12][0-9]|3[01])$/;
    } else {
      regExp = /(^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$)/;
    }
    return regExp;
  }

  dateConvertor(country, date, id?) {
    let convertedDate;
    switch (id) {
      case 'rent-review':
      case 'lease-incentive':
        if (country === 'JPN') {
          convertedDate = moment(date, 'YYYY/MM/DD');
        } else {
          convertedDate = moment(moment(date, 'DD/MM/YYYY'));
        }
        break;
      default:
        if (country === 'JPN') {
          convertedDate = moment(date, 'YYYY/MM/DD').format('YYYY-MM-DD');
        } else {
          convertedDate = moment(moment(date, 'DD/MM/YYYY')).format('YYYY-MM-DD');
        }
        break;
    }
    return convertedDate;
  }

  getSubFeeType() {
    return this.api.get(`/api/transactions/subfeetype/`)
      .pipe(
        map(response => {
          if (response && response.results) {
            return response.results;
          }
        })
      );
  }

  copyTransaction(data: { transaction_id: string, opportunity_id?: string, installment_id?: string }) {
    return this.api.post(`/api/transactions/copy/${data.transaction_id}/`, data)
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  updateGUIReceipt(transactionId, data) {
    return this.api.put(`/api/transactions/invoiced/gui_receipt_update/${transactionId}/`, data);
  }

  saveUserSurveyResponse(response_value) {
    return this.api.put(`/api/activedirectory/survey_response/`, { 'response_value': response_value })
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  getRetailBrands(q: string): Observable<any> {
    return this.api.get(`/api/transactions/search_brand_name_es/?name=${q}`).pipe((response: any) => response);
  }

  getShipToCustomerInfo(userCountry: string, myFinance: string | null, addressSeqNum: number, customer?: string) {
    let shipToCustObject = {};
    if (userCountry === 'IND' && myFinance === 'peoplesoft') {
      if (customer) {
        shipToCustObject = {
          ...shipToCustObject,
          SHIP_TO_CUST_ID: customer
        }
      }
      if (addressSeqNum) {
        shipToCustObject = {
          ...shipToCustObject,
          SHIP_TO_ADDR_NUM: addressSeqNum
        }
      }
    }
    return shipToCustObject;
  }

  saveInvoiceAddressForm(transactionId: number, invoiceAddress) {
    return this.api.put(`/api/transactions/${transactionId}/invoice_address/${invoiceAddress.id}/`, invoiceAddress)
      .pipe(map(response => response)
      );
  }

  getInvoiceAddressForm(transactionId: number) {
    return this.api.get(`/api/transactions/${transactionId}/invoice_address/`)
      .pipe(map(response => response)
      );
  }

  postInvoiceAddress(transactionId: number, invoiceAddressId: DataObject) {
    return this.api.post(`/api/transactions/${transactionId}/invoice_address/`, invoiceAddressId)
      .pipe(map(response => response)
      );
  }

  deleteInvoiceAddress(transactionId, invoiceAddressId) {
    return this.api.delete(`/api/transactions/${transactionId}/invoice_address/${invoiceAddressId}/`)
      .pipe(map(response => response)
      );
  }

  getLandingData(
    page: number = 1,
    pageSize: number = 20,
    transStatus: string,
    transType: string,
    startDate: string | null,
    endDate: string | null,
    querySearch: string | null,
    filterBy: 'MTD' | 'QTD' | 'YTD' | null,
    sortBy?: string | null,
    sortDirection?: string | null,
    project?: string | null,
    transactionStatus?: string | null,
    download?: boolean
  ) {
    let params = `page=${page}&page_size=${pageSize}`;
    if (transStatus) {
      params += `&status=${transStatus}`;
    }
    if (transType) {
      params += `&trans=${transType}`;
    }
    if (startDate && endDate) {
      params += `&start_date=${startDate}&end_date=${endDate}`;
    }
    if (querySearch) {
      const stringifyQuery = encodeURIComponent(querySearch);
      params += `&q=${stringifyQuery}`;
    }
    if (filterBy) {
      params += `&filter_by=${filterBy}`;
    }
    if (sortBy) {
      params += `&sort_by=${sortBy}`;
    }
    if (sortDirection) {
      params += `&sort_dir=${sortDirection}`;
    }
    if (project) {
      params += `&project=${project}`;
    }
    if (transactionStatus) {
      params += `&transaction_status=${transactionStatus}`;
    }
    if (download) {
      params += `&download=true`;
    }
    return this.api.get(`/api/transactions/?${params}`)
      .pipe(
        map(response => {
          if (response?.error?.status || response?.status >= 400) {
            throw response;
          }
          
          // Handle download response differently - it might return data directly
          if (download) {
            // For download, return the response as-is, it might be structured differently
            if (Array.isArray(response)) {
              return response;
            } else if (response.results) {
              return response.results;
            } else {
              return response;
            }
          }
          
          // Normal response processing
          if (!response || !response.results) {
            response.results = [];
          }
          response.results.map(transaction => {
            return transaction['property_address'] = transaction.properties[0] || {
              "id": null,
              "building_name": null,
              "address_line1": null,
              "address_line2": null,
              "address_line3": null,
              "address_line4": null,
              "address_line5": null,
              "address_line6": null,
              "postal_code": null,
              "country": null,
              "locality": null,
              "region": null
            };
          });
          return response;
        }),
        catchError(error => {
          return this.errorHandler.handleError(error, { results: [] });
        })
      );
  }

  getTransactionStatusChoices(status): Observable<any> {
    const params = status ? { status } : {};
    return this.api.get('/api/transactions/transaction_status_choices/', { params })
      .pipe(
        map(response => {
          if (response?.error?.status || response?.status >= 400) {
            throw response;
          }
          return response;
        }),
        catchError(error => {
          return this.errorHandler.handleError(error, { status_choices: [] });
        })
      );
  }

  isAdjustBillLines(transaction: DataObject): boolean {
    return (
      transaction?.status_name === 'Adjust Bill Lines' || 
      transaction?.access_to_adjust_bill_lines === true || 
      transaction?.latest_non_error_status === 19
    );
  }

}
